import "core-js/modules/es.array.reverse.js";
var _class, _descriptor, _descriptor2;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { action, autorun, computed, makeObservable, observable } from "mobx";
import ClockRange from "terriajs-cesium/Source/Core/ClockRange";
import JulianDate from "terriajs-cesium/Source/Core/JulianDate";
import filterOutUndefined from "../Core/filterOutUndefined";
import ReferenceMixin from "../ModelMixins/ReferenceMixin";
import { DATE_SECONDS_PRECISION } from "../ModelMixins/TimeVarying";
import DefaultTimelineModel from "./DefaultTimelineModel";
import CommonStrata from "./Definition/CommonStrata";
const DEFAULT_TIMELINE_MODEL_ID = "defaultTimeline";

/**
 * Manages a stack of all the time-varying datasets currently attached to the timeline. Provides
 * access to the current top dataset so that it can be displayed to the user.
 *
 * @constructor
 */
let TimelineStack = (_class = class TimelineStack {
  constructor(terria, clock) {
    this.terria = terria;
    this.clock = clock;
    /**
     * The stratum of each layer in the stack in which to store the current time as the clock ticks.
     */
    _defineProperty(this, "tickStratumId", CommonStrata.user);
    _initializerDefineProperty(this, "items", _descriptor, this);
    _initializerDefineProperty(this, "defaultTimeVarying", _descriptor2, this);
    _defineProperty(this, "_disposeClockAutorun", void 0);
    _defineProperty(this, "_disposeTickSubscription", void 0);
    makeObservable(this);
  }
  activate() {
    // Keep the Cesium clock in sync with the top layer's clock.
    this._disposeClockAutorun = autorun(() => {
      const topLayer = this.top;
      if (!topLayer || !topLayer.currentTimeAsJulianDate) {
        this.clock.shouldAnimate = false;
        return;
      }
      this.clock.currentTime = JulianDate.clone(topLayer.currentTimeAsJulianDate, this.clock.currentTime);
      this.clock.startTime = offsetIfUndefined(-43200.0, topLayer.currentTimeAsJulianDate, topLayer.startTimeAsJulianDate, this.clock.startTime);
      this.clock.stopTime = offsetIfUndefined(43200.0, topLayer.currentTimeAsJulianDate, topLayer.stopTimeAsJulianDate, this.clock.stopTime);
      if (topLayer.multiplier !== undefined) {
        this.clock.multiplier = topLayer.multiplier;
      } else {
        this.clock.multiplier = 60.0;
      }
      this.clock.shouldAnimate = !topLayer.isPaused;
      this.clock.clockRange = ClockRange.LOOP_STOP;
      if (this._disposeTickSubscription === undefined) {
        // We should start synchronizing only after first run of this autorun so that
        // the clock parameters are set correctly.
        this._disposeTickSubscription = this.clock.onTick.addEventListener(() => {
          this.syncToClock(this.tickStratumId);
        });
      }
    });
  }
  deactivate() {
    if (this._disposeClockAutorun) {
      this._disposeClockAutorun();
    }
    if (this._disposeTickSubscription) {
      this._disposeTickSubscription();
      this._disposeTickSubscription = undefined;
    }
  }

  /**
   * The topmost time-series layer, or undefined if there is no such layer in the stack.
   */
  get top() {
    // Find the first item with a current, start, and stop time.
    // Use the default if there isn't one.
    return this.items.find(item => {
      const dereferenced = ReferenceMixin.isMixedInto(item) && item.target ? item.target : item;
      return dereferenced.currentTimeAsJulianDate !== undefined && dereferenced.startTimeAsJulianDate !== undefined && dereferenced.stopTimeAsJulianDate !== undefined;
    }) || this.defaultTimeVarying;
  }
  get itemIds() {
    return filterOutUndefined(this.items.map(item => item.uniqueId));
  }

  /**
   * Determines if the stack contains a given item.
   * @param item The item to check.
   * @returns True if the stack contains the item; otherwise, false.
   */
  contains(item) {
    return this.items.indexOf(item) >= 0;
  }

  /**
   * Adds the supplied {@link TimeVarying} to the top of the stack. If the item is already in the stack, it will be moved
   * rather than added twice.
   *
   * @param item
   */
  addToTop(item) {
    const currentIndex = this.items.indexOf(item);
    this.items.unshift(item);
    if (currentIndex > -1) {
      this.items.splice(currentIndex, 1);
    }
  }

  /**
   * Removes a layer from the stack, no matter what its location. If the layer is currently at the top, the value of
   * {@link TimelineStack#topLayer} will change.
   *
   * @param item;
   */
  remove(item) {
    const index = this.items.indexOf(item);
    this.items.splice(index, 1);
  }

  /**
   * Removes all layers.
   */
  removeAll() {
    this.items = [];
  }

  /**
   * Promotes the supplied {@link CatalogItem} to the top of the stack if it is already in the stack. If the item is not
   * already in the stack it won't be added.
   *
   * @param item
   */
  promoteToTop(item) {
    const currentIndex = this.items.indexOf(item);
    if (currentIndex > -1) {
      this.addToTop(item);
    }
  }

  /**
   * Synchronizes all layers in the stack to the current time and the paused state of the provided clock.
   * Synchronizes the {@link TimelineStack#top} to the clock's `startTime`, `endTime`, and `multiplier`.
   * @param stratumId The stratum in which to modify properties.
   * @param clock The clock to sync to.
   */
  syncToClock(stratumId) {
    const clock = this.clock;
    const currentTime = JulianDate.toIso8601(clock.currentTime, DATE_SECONDS_PRECISION);
    const isPaused = !clock.shouldAnimate;
    if (this.top) {
      this.top.setTrait(stratumId, "startTime", JulianDate.toIso8601(clock.startTime, DATE_SECONDS_PRECISION));
      this.top.setTrait(stratumId, "stopTime", JulianDate.toIso8601(clock.stopTime, DATE_SECONDS_PRECISION));
      this.top.setTrait(stratumId, "multiplier", clock.multiplier);
    }
    for (let i = 0; i < this.items.length; ++i) {
      const layer = this.items[i];
      layer.setTrait(stratumId, "currentTime", currentTime);
      layer.setTrait(stratumId, "isPaused", isPaused);
    }
    if (this.defaultTimeVarying) {
      this.defaultTimeVarying.setTrait(stratumId, "currentTime", currentTime);
      this.defaultTimeVarying.setTrait(stratumId, "isPaused", isPaused);
    }
  }
  setAlwaysShowTimeline() {
    let show = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    if (show) {
      this.defaultTimeVarying = this.getOrCreateDefaultTimelineModel();
    } else {
      if (this.defaultTimeVarying) {
        // Unregister the model so that it doesn't appear in share links
        this.terria.removeModelReferences(this.defaultTimeVarying);
      }
      this.defaultTimeVarying = undefined;
    }
    this.terria.currentViewer.notifyRepaintRequired();
  }
  get alwaysShowingTimeline() {
    return this.defaultTimeVarying !== undefined && this.defaultTimeVarying.startTimeAsJulianDate !== undefined && this.defaultTimeVarying.stopTimeAsJulianDate !== undefined && this.defaultTimeVarying.currentTimeAsJulianDate !== undefined;
  }
  getOrCreateDefaultTimelineModel() {
    let model = this.terria.getModelById(DefaultTimelineModel, DEFAULT_TIMELINE_MODEL_ID);
    if (!model) {
      model = new DefaultTimelineModel(DEFAULT_TIMELINE_MODEL_ID, this.terria);
      this.terria.addModel(model);
    }
    return model;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "items", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "defaultTimeVarying", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "top", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "top"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "itemIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "itemIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addToTop", [action], Object.getOwnPropertyDescriptor(_class.prototype, "addToTop"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "remove", [action], Object.getOwnPropertyDescriptor(_class.prototype, "remove"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "promoteToTop", [action], Object.getOwnPropertyDescriptor(_class.prototype, "promoteToTop"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "syncToClock", [action], Object.getOwnPropertyDescriptor(_class.prototype, "syncToClock"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setAlwaysShowTimeline", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setAlwaysShowTimeline"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "alwaysShowingTimeline", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "alwaysShowingTimeline"), _class.prototype), _class);
export { TimelineStack as default };
function offsetIfUndefined(offsetSeconds, baseTime, time, result) {
  if (time === undefined) {
    return JulianDate.addSeconds(baseTime, offsetSeconds, result || new JulianDate());
  } else {
    return JulianDate.clone(time, result);
  }
}